// frontend/src/pages/cargo/CreateCover.js
import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Eye, Download, X as XIcon, Paperclip } from 'lucide-react';

// ========== MODE-AWARE PORT SELECT ==========
const ModeAwarePortSelect = ({
    value, onChange, placeholder, isRequired, label, name, error, touched, mode,
}) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedCode, setSelectedCode] = useState('');
    const debounceRef = useRef(null);

    useEffect(() => {
        setSelectedOption(value ? { value, label: value } : null);
    }, [value]);

    const loadOptions = useCallback(async (inputValue) => {
        if (!inputValue || inputValue.trim().length < 2) return [];
        if (!mode) return [];
        try {
            const { data } = await api.get('/cargo/dmvic/search-locations', {
                params: { q: inputValue.trim(), type: 'port', mode: mode.toLowerCase() },
            });
            return (data || []).map((row) => ({
                value: row.port_name,
                label: row.country_name ? `${row.port_name} — ${row.country_name}` : row.port_name,
                code: row.port_code,
                locode: row.locode || '',
                isSea: row.is_sea,
            }));
        } catch (err) {
            console.error('[ModeAwarePortSelect] Search failed:', err);
            return [];
        }
    }, [mode]);

    const debouncedLoadOptions = useCallback((inputValue, callback) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            const options = await loadOptions(inputValue);
            callback(options);
        }, 250);
    }, [loadOptions]);

    const handleChange = async (option) => {
        if (!option) {
            setSelectedOption(null);
            setSelectedCode('');
            onChange({ target: { name, value: '' } });
            onChange({ target: { name: `${name}_code`, value: '' } });
            return;
        }
        try {
            const { data } = await api.get('/cargo/dmvic/resolve-location', {
                params: { name: option.value, type: 'port', mode: (mode || '').toLowerCase() },
            });
            if (!data?.found || !data?.dmvic_code) {
                console.warn('[ModeAwarePortSelect] backend rejected selection:', option.value);
                return;
            }
            setSelectedOption(option);
            setSelectedCode(data.dmvic_code);
            onChange({ target: { name, value: option.value } });
            onChange({ target: { name: `${name}_code`, value: data.dmvic_code } });
        } catch (err) {
            console.error('[ModeAwarePortSelect] resolve failed:', err);
        }
    };

    const showError = error && touched;
    const disabled = !mode;

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                {label} {isRequired && <span className="text-red-500">*</span>}
            </label>
            <AsyncSelect
                cacheOptions
                loadOptions={debouncedLoadOptions}
                value={selectedOption}
                onChange={handleChange}
                placeholder={placeholder || `Type to search ${mode || ''}...`}
                isClearable
                isSearchable
                isDisabled={disabled}
                defaultOptions={false}
                classNamePrefix="react-select"
                styles={{
                    control: (provided, state) => ({
                        ...provided,
                        borderRadius: '12px',
                        borderColor: showError ? '#ef4444' : state.isFocused ? '#3a663f' : '#e5e7eb',
                        padding: '2px 4px',
                        minHeight: '48px',
                        boxShadow: showError
                            ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                            : state.isFocused
                                ? '0 0 0 3px rgba(58, 102, 63, 0.1)'
                                : 'none',
                        '&:hover': { borderColor: showError ? '#ef4444' : '#3a663f' },
                    }),
                    menu: (provided) => ({
                        ...provided,
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                        zIndex: 9999,
                        overflow: 'hidden',
                    }),
                    option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? '#f0f7f0' : 'transparent',
                        color: state.isSelected ? '#ffffff' : state.isFocused ? '#3a663f' : '#1f2937',
                        padding: '10px 16px',
                        cursor: 'pointer',
                    }),
                    input: (p) => ({ ...p, color: '#1f2937' }),
                    singleValue: (p) => ({ ...p, color: '#1f2937' }),
                    placeholder: (p) => ({ ...p, color: '#9ca3af' }),
                    noOptionsMessage: (p) => ({ ...p, color: '#6b7280', padding: '12px' }),
                }}
                noOptionsMessage={({ inputValue }) => {
                    if (!mode) return 'Select a transport mode first';
                    if (!inputValue || inputValue.length < 2) return 'Type at least 2 characters...';
                    return `No ${mode === 'Air' ? 'airports' : 'ports'} found`;
                }}
                loadingMessage={() => 'Searching...'}
            />
            {selectedCode && (
                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1 font-mono">
                    Code: {selectedCode}
                </p>
            )}
            {showError && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
        </div>
    );
};

// ========== LOCAL DATE HELPERS ==========
function toLocalISODate(d) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}
function parseLocalISODate(s) {
    if (!s || typeof s !== 'string') return null;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const [, y, mo, d] = m;
    const dt = new Date(Number(y), Number(mo) - 1, Number(d));
    if (
        dt.getFullYear() !== Number(y) ||
        dt.getMonth() !== Number(mo) - 1 ||
        dt.getDate() !== Number(d)
    ) return null;
    return dt;
}
function addLocalDays(isoDate, days) {
    const d = parseLocalISODate(isoDate);
    if (!d) return '';
    const copy = new Date(d.getTime());
    copy.setDate(copy.getDate() + days);
    return toLocalISODate(copy);
}
function todayLocalISO() {
    return toLocalISODate(new Date());
}

// ========== DEFAULT FORM DEFINITION ==========
const defaultForm = {
    trade_type: 'Import',
    mode_of_transport: 'Sea',
    idf_number: '',
    ucr_number: '',
    commercial_invoice_number: '',
    vessel_name: '',
    cargo_category: '',
    cargo_type: '',
    currency: 'KES',
    voyage_from_country: '',
    loading_at: '',
    loading_at_code: '',
    voyage_to_country: 'Kenya',
    port_of_discharge: '',
    port_of_discharge_code: '',
    final_destination: '',
    transshipment_at: '',
    cover_start_date: todayLocalISO(),
    cover_end_date: addLocalDays(todayLocalISO(), 90),
    nature_of_goods: '',
    acknowledgment: false,
    clearing_agent_pin: '',
    open_cover_number: '',
    consignee_email: '',
    consignee_phone: '',
    consignee_country_of_origin: '',
    consignee_county: '',
    consignee_city: '',
    consignee_postal_code: '',
    consignee_address: '',
    files: { idf_doc: null, ucr_doc: null, invoice_doc: null },
};

const SECTIONS = [
    { id: 'policy', icon: '📋', label: 'Policy' },
    { id: 'idf', icon: '🔍', label: 'IDF & Agent' },
    { id: 'vessel', icon: '🚢', label: 'Vessel' },
    { id: 'voyage', icon: '🌍', label: 'Voyage' },
    { id: 'cover', icon: '📅', label: 'Cover' },
    { id: 'consignee', icon: '👤', label: 'Consignee' },
];

const SECTION_REQUIRED = {
    policy: ['trade_type', 'mode_of_transport', 'currency'],
    idf: ['idf_number', 'clearing_agent_pin', 'commercial_invoice_number'],
    vessel: ['vessel_name'],
    voyage: [
        'voyage_from_country', 'loading_at', 'port_of_discharge', 'final_destination',
    ],
    cover: ['cover_start_date', 'cover_end_date', 'nature_of_goods'],
    consignee: [
        'consignee_email', 'consignee_phone', 'consignee_country_of_origin',
        'consignee_county', 'consignee_city', 'consignee_postal_code', 'consignee_address',
    ],
};

const MODE_LABELS = {
    Sea: { loading: 'Port of Loading', discharge: 'Port of Discharge' },
    Air: { loading: 'Airport of Loading', discharge: 'Airport of Discharge' },
};

// ========== NEW: ATTACH FILE INPUT ==========
// Small picker that shows the selected file with View / Download / Remove
// controls. Purely a UI upgrade — it drives the same `onChange` handler the
// original <input type="file"> used, so no logic changes.
const AttachFileInput = memo(function AttachFileInput({
    label,
    name,
    file,
    onChange,
    accept,
}) {
    const inputRef = useRef(null);

    const handleView = useCallback(() => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }, [file]);

    const handleDownload = useCallback(() => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [file]);

    const handleRemove = useCallback(() => {
        if (inputRef.current) inputRef.current.value = '';
        // Reuse the same event shape the original handler expects.
        onChange({
            target: {
                name,
                files: { 0: undefined, length: 0, item: () => undefined },
            },
        });
    }, [name, onChange]);

    return (
        <div className="mt-1.5">
            <label className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1.5 cursor-pointer hover:text-gray-700 dark:hover:text-slate-300 transition-colors">
                <Paperclip className="w-3.5 h-3.5" />
                {file ? `Attached: ${file.name}` : `Attach ${label}`}
                <input
                    ref={inputRef}
                    type="file"
                    name={name}
                    accept={accept}
                    onChange={onChange}
                    className="hidden"
                />
            </label>

            {file && (
                <div className="mt-1.5 flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-2.5 py-2 text-xs">
                    <span className="text-gray-700 dark:text-slate-300 truncate flex-1" title={file.name}>
                        📄 {file.name}{' '}
                        <span className="text-gray-400">
                            ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                    </span>
                    <button
                        type="button"
                        onClick={handleView}
                        title="View"
                        aria-label="View file"
                        className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex-shrink-0"
                    >
                        <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={handleDownload}
                        title="Download"
                        aria-label="Download file"
                        className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors flex-shrink-0"
                    >
                        <Download className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={handleRemove}
                        title="Remove"
                        aria-label="Remove file"
                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
                    >
                        <XIcon className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
});

// ========== MAIN COMPONENT ==========
export default function CreateCover({ onComplete, prefillData, prefillIdfData, initialFormData }) {
    const { user } = useSelector((state) => state.auth);

    const [form, setForm] = useState(() => {
        if (initialFormData && Object.keys(initialFormData).length > 0) {
            return {
                ...defaultForm,
                ...initialFormData,
                files: { idf_doc: null, ucr_doc: null, invoice_doc: null },
                acknowledgment: false,
            };
        }
        return { ...defaultForm };
    });

    const [loading, setLoading] = useState(false);
    const [idfLoading, setIdfLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeSection, setActiveSection] = useState('policy');
    const [formErrors, setFormErrors] = useState({});
    const [touched, setTouched] = useState({});

    const [autoFilled, setAutoFilled] = useState({});

    const [clearingAgents, setClearingAgents] = useState([]);
    const [loadingAgents, setLoadingAgents] = useState(true);
    const [selectedAgentPin, setSelectedAgentPin] = useState('');
    const [showAddAgent, setShowAddAgent] = useState(false);
    const [newAgent, setNewAgent] = useState({
        agent_name: '', kra_pin: '', company_name: '', phone: '', email: '',
    });
    const [savingAgent, setSavingAgent] = useState(false);
    const [agentError, setAgentError] = useState('');
    const [agentSuccess, setAgentSuccess] = useState('');

    const formTopRef = useRef(null);
    const fetchedAgentsRef = useRef(false);

    // ---------- Prefill from quote ----------
    useEffect(() => {
        if (prefillData) {
            const prefillForm = {
                trade_type: prefillData.trade_type || 'Import',
                mode_of_transport: (prefillData.mode_of_transport === 'Air') ? 'Air' : 'Sea',
                idf_number: prefillData.idf_number || prefillData.invoice_number || '',
                ucr_number: prefillData.ucr_number || '',
                commercial_invoice_number: prefillData.invoice_number || '',
                vessel_name: prefillData.vessel_name || '',
                cargo_category: prefillData.cargo_category || '',
                cargo_type: prefillData.cargo_type || '',
                currency: (prefillData.currency || 'KES').toUpperCase() === 'USD' ? 'USD' : 'KES',
                voyage_from_country: prefillData.from_location || '',
                loading_at: prefillData.loading_at || prefillData.from_location || '',
                loading_at_code: prefillData.loading_at_code || '',
                voyage_to_country: prefillData.to_location || 'Kenya',
                port_of_discharge: prefillData.port_of_discharge || prefillData.to_location || '',
                port_of_discharge_code: prefillData.port_of_discharge_code || '',
                final_destination: prefillData.final_destination || '',
                transshipment_at: prefillData.transshipment_at || '',
                cover_start_date: prefillData.coverage_start || defaultForm.cover_start_date,
                cover_end_date: prefillData.coverage_end || addLocalDays(defaultForm.cover_start_date, 90),
                nature_of_goods: prefillData.interests_insured || prefillData.nature_of_goods || '',
                clearing_agent_pin: prefillData.clearing_agent_pin || prefillIdfData?.agents_company_pin || '',
                open_cover_number: prefillData.open_cover_number || '',
                consignee_email: prefillData.consignee_email || '',
                consignee_phone: prefillData.consignee_phone || '',
                consignee_country_of_origin: prefillData.consignee_country_of_origin || '',
                consignee_county: prefillData.consignee_county || '',
                consignee_city: prefillData.consignee_city || '',
                consignee_postal_code: prefillData.consignee_postal_code || '',
                consignee_address: prefillData.consignee_address || '',
                acknowledgment: false,
                files: { idf_doc: null, ucr_doc: null, invoice_doc: null },
            };

            setForm((prev) => ({ ...prev, ...prefillForm, files: prev.files || prefillForm.files }));

            if (prefillForm.clearing_agent_pin) {
                setSelectedAgentPin(prefillForm.clearing_agent_pin);
            }

            const prefilled = {};
            Object.entries(prefillForm).forEach(([k, v]) => {
                if (v && k !== 'files' && k !== 'acknowledgment') prefilled[k] = true;
            });
            setAutoFilled(prefilled);
        }
    }, [prefillData, prefillIdfData]);

    // ---------- Fetch clearing agents once ----------
    useEffect(() => {
        if (fetchedAgentsRef.current) return;
        fetchedAgentsRef.current = true;
        fetchClearingAgents();
    }, []);

    const fetchClearingAgents = async () => {
        setLoadingAgents(true);
        try {
            const { data } = await api.get('/clearing-agents');
            setClearingAgents(data || []);
            if ((data || []).length > 0 && !selectedAgentPin) {
                const first = data[0].kra_pin;
                setSelectedAgentPin(first);
                setForm((prev) => ({ ...prev, clearing_agent_pin: first }));
            }
        } catch (err) {
            console.error('[CreateCover] Failed to load clearing agents:', err.message);
        } finally {
            setLoadingAgents(false);
        }
    };

    const handleSaveAgent = async (e) => {
        e.preventDefault();
        if (!newAgent.agent_name || !newAgent.kra_pin) {
            setAgentError('Agent name and KRA PIN are required');
            return;
        }
        setSavingAgent(true);
        setAgentError('');
        setAgentSuccess('');
        try {
            await api.post('/clearing-agents', newAgent);
            setAgentSuccess('✅ Agent saved successfully!');
            setNewAgent({ agent_name: '', kra_pin: '', company_name: '', phone: '', email: '' });
            await fetchClearingAgents();
            setTimeout(() => {
                setShowAddAgent(false);
                setAgentSuccess('');
            }, 1800);
        } catch (err) {
            setAgentError(err.response?.data?.error || 'Failed to save agent');
        } finally {
            setSavingAgent(false);
        }
    };

    // ---------- Field handlers ----------
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setTouched((prev) => ({ ...prev, [name]: true }));
        setFormErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
        setAutoFilled((prev) => (prev[name] ? { ...prev, [name]: false } : prev));
    };

    const handleModeChange = (e) => {
        const nextMode = e.target.value;
        setForm((prev) => ({
            ...prev,
            mode_of_transport: nextMode,
            loading_at: '',
            loading_at_code: '',
            port_of_discharge: '',
            port_of_discharge_code: '',
        }));
        setTouched((prev) => ({
            ...prev,
            mode_of_transport: true,
            loading_at: false,
            port_of_discharge: false,
        }));
        setFormErrors((prev) => ({
            ...prev,
            loading_at: '',
            port_of_discharge: '',
        }));
        setAutoFilled((prev) => ({
            ...prev,
            loading_at: false,
            port_of_discharge: false,
        }));
    };

    const handleAgentSelect = (e) => {
        const pin = e.target.value;
        setSelectedAgentPin(pin);
        setForm((prev) => ({ ...prev, clearing_agent_pin: pin }));
        setTouched((prev) => ({ ...prev, clearing_agent_pin: true }));
        setFormErrors((prev) => (prev.clearing_agent_pin ? { ...prev, clearing_agent_pin: '' } : prev));
    };

    const handleAgentPinInput = (e) => {
        const pin = e.target.value;
        setSelectedAgentPin(pin);
        setForm((prev) => ({ ...prev, clearing_agent_pin: pin }));
        setTouched((prev) => ({ ...prev, clearing_agent_pin: true }));
        setFormErrors((prev) => (prev.clearing_agent_pin ? { ...prev, clearing_agent_pin: '' } : prev));
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        // The AttachFileInput's Remove button passes an object with `length: 0`.
        const picked = files && files.length ? files[0] : null;
        setForm((prev) => ({ ...prev, files: { ...prev.files, [name]: picked } }));
    };

    // ---------- IDF lookup ----------
    const handleIDFLookup = async () => {
        if (!form.idf_number.trim()) { setError('Please enter an IDF number first.'); return; }
        if (!selectedAgentPin.trim()) { setError('Please select a clearing agent PIN first.'); return; }

        setIdfLoading(true);
        setError('');
        try {
            const { data } = await api.post('/cargo/idf-lookup', {
                idf_number: form.idf_number.trim(),
                agents_company_pin: selectedAgentPin.trim(),
            });
            const body = data.body;
            const consignee = body.consignee?.[0] || body.consigenee?.[0] || {};
            const complimentaries = body.complimentaries || {};

            let goodsSummary = '';
            if (body.items && body.items.length) {
                goodsSummary = body.items
                    .map((item, idx) => `${idx + 1}. ${item.tariff_goods_desc}`)
                    .join('\n');
            }

            const idfCurrency = String(body.invoice_currency || '').toUpperCase();
            const resolvedCurrency = (idfCurrency === 'USD' || idfCurrency === 'KES') ? idfCurrency : null;
            const idfMode = (body.mode_of_transport || '').toLowerCase() === 'air' ? 'Air' : 'Sea';

            const updates = {
                ucr_number: body.ucr_number,
                commercial_invoice_number: complimentaries.idf_invoice_num,
                mode_of_transport: idfMode,
                currency: resolvedCurrency || undefined,
                voyage_from_country: body.origin_country,
                voyage_to_country: consignee.destination_country,
                port_of_discharge: body.delivery_place,
                port_of_discharge_code: '',
                final_destination: body.delivery_place
                    || body.final_destination
                    || consignee.destination_country
                    || '',
                nature_of_goods: goodsSummary,
                consignee_email: consignee.email,
                consignee_phone: consignee.phone_number || consignee.phone,
                consignee_country_of_origin: body.origin_country,
                consignee_county: consignee.county,
                consignee_city: consignee.city,
                consignee_postal_code: consignee.postal_code,
                consignee_address: consignee.postal_address || consignee.address,
            };

            setForm((prev) => {
                const next = { ...prev };
                Object.entries(updates).forEach(([k, v]) => {
                    if (v !== undefined && v !== null && v !== '') next[k] = v;
                });
                return next;
            });

            setAutoFilled((prev) => {
                const next = { ...prev };
                Object.entries(updates).forEach(([k, v]) => {
                    if (v !== undefined && v !== null && v !== '') next[k] = true;
                });
                return next;
            });

            setError('✅ IDF data retrieved successfully!');
            setTimeout(() => setError(''), 3000);
        } catch (err) {
            const status = err.response?.status;
            const code = err.response?.data?.code;
            const retryAfter = err.response?.data?.retry_after;

            if (code === 'IRA_TIMEOUT' || status === 504) {
                setError(`The IRA system is taking longer than usual to respond. Please try again${retryAfter ? ` in about ${retryAfter} seconds` : ' shortly'}.`);
            } else if (code === 'IRA_UPSTREAM_ERROR' || status === 502) {
                setError('The IRA system is currently unavailable. Please try again in a few minutes.');
            } else if (code === 'IRA_AUTH_ERROR') {
                setError('We could not authenticate with the IRA system. Please contact support.');
            } else if (code === 'IDF_NOT_FOUND' || status === 404) {
                setError('This IDF number was not found in the IRA system. Please check and re-enter it.');
            } else if (status === 400) {
                setError(err.response?.data?.error || 'Please check the IDF number and clearing agent PIN, then try again.');
            } else if (status === 429) {
                setError('Too many attempts. Please wait a moment and try again.');
            } else if (status === 500) {
                setError('Something went wrong on our side. Please try again, or contact support if it persists.');
            } else {
                setError(err.response?.data?.error || 'IDF lookup failed. Please check the IDF number and try again.');
            }
        } finally {
            setIdfLoading(false);
        }
    };

    // ---------- Date handling ----------
    const handleStartDateChange = (e) => {
        const startDate = e.target.value;
        setForm((prev) => {
            const updates = { cover_start_date: startDate };
            if (startDate) {
                updates.cover_end_date = addLocalDays(startDate, 90);
            } else {
                updates.cover_end_date = '';
            }
            return { ...prev, ...updates };
        });
        setTouched((prev) => ({ ...prev, cover_start_date: true, cover_end_date: true }));
    };

    const getMaxEndDate = () => {
        if (!form.cover_start_date) return '';
        return addLocalDays(form.cover_start_date, 90);
    };

    const isEndDateValid = () => {
        if (!form.cover_start_date || !form.cover_end_date) return true;
        const start = parseLocalISODate(form.cover_start_date);
        const end = parseLocalISODate(form.cover_end_date);
        if (!start || !end) return false;
        const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
        return diffDays === 90;
    };

    // ---------- Validation ----------
    const validateField = (field, value) => {
        if (!value || String(value).trim() === '') return 'This field is required';
        if (field === 'consignee_email' && !/\S+@\S+\.\S+/.test(value)) {
            return 'Please enter a valid email address';
        }
        if (field === 'currency' && !['KES', 'USD'].includes(value)) {
            return 'Currency must be KES or USD';
        }
        if (field === 'mode_of_transport' && !['Sea', 'Air'].includes(value)) {
            return 'Mode of transport must be Sea or Air';
        }
        return '';
    };

    const validateSection = (sectionId) => {
        const errors = {};
        (SECTION_REQUIRED[sectionId] || []).forEach((field) => {
            const err = validateField(field, form[field]);
            if (err) errors[field] = err;
        });
        return errors;
    };

    const validateFullForm = () => {
        const all = {};
        Object.keys(SECTION_REQUIRED).forEach((section) => {
            Object.assign(all, validateSection(section));
        });
        return all;
    };

    // ---------- Navigation ----------
    const goToSection = (sectionId) => {
        setActiveSection(sectionId);
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleNext = () => {
        const errs = validateSection(activeSection);
        if (Object.keys(errs).length) {
            setFormErrors((prev) => ({ ...prev, ...errs }));
            const newTouched = {};
            Object.keys(errs).forEach((k) => (newTouched[k] = true));
            setTouched((prev) => ({ ...prev, ...newTouched }));
            setError('Please fix the errors in this section before continuing.');
            return;
        }
        const idx = SECTIONS.findIndex((s) => s.id === activeSection);
        if (idx >= 0 && idx < SECTIONS.length - 1) {
            goToSection(SECTIONS[idx + 1].id);
            setError('');
        }
    };

    const handleBack = () => {
        const idx = SECTIONS.findIndex((s) => s.id === activeSection);
        if (idx > 0) goToSection(SECTIONS[idx - 1].id);
    };

    // ---------- Submit ----------
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.acknowledgment) {
            setError('Please accept the terms and conditions to continue.');
            return;
        }

        const errors = validateFullForm();
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            const touchedAll = {};
            Object.keys(errors).forEach((k) => (touchedAll[k] = true));
            setTouched((prev) => ({ ...prev, ...touchedAll }));

            const firstErrorField = Object.keys(errors)[0];
            const sectionWithError = Object.keys(SECTION_REQUIRED).find((s) =>
                SECTION_REQUIRED[s].includes(firstErrorField)
            );
            if (sectionWithError) goToSection(sectionWithError);

            setError(`Please fill in all required fields (${Object.keys(errors).length} missing).`);
            return;
        }

        if (!['Sea', 'Air'].includes(form.mode_of_transport)) {
            setError('Mode of transport must be Sea or Air.');
            return;
        }

        if (form.cover_start_date && form.cover_end_date) {
            const start = parseLocalISODate(form.cover_start_date);
            const end = parseLocalISODate(form.cover_end_date);
            if (!start || !end) { setError('Invalid cover dates.'); return; }
            const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
            if (diffDays !== 90) { setError('Cover period must be exactly 90 days.'); return; }
            if (start < parseLocalISODate(todayLocalISO())) {
                setError('Cover start date cannot be in the past.');
                return;
            }
        }

        setLoading(true);
        setError('');
        try {
            const fd = new FormData();
            Object.keys(form).forEach((key) => {
                if (key === 'files') return;
                fd.append(key, form[key] || '');
            });
            if (form.files.idf_doc) fd.append('idf_doc', form.files.idf_doc);
            if (form.files.ucr_doc) fd.append('ucr_doc', form.files.ucr_doc);
            if (form.files.invoice_doc) fd.append('invoice_doc', form.files.invoice_doc);

            const { data } = await api.post('/covers', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const coverFormData = {
                idf_number: form.idf_number || '',
                vessel_name: form.vessel_name || '',
                loading_at: form.loading_at || '',
                loading_at_code: form.loading_at_code || '',
                port_of_discharge: form.port_of_discharge || '',
                port_of_discharge_code: form.port_of_discharge_code || '',
                final_destination: form.final_destination || '',
                open_cover_number: form.open_cover_number || '',
                voyage_from_country: form.voyage_from_country || '',
                voyage_to_country: form.voyage_to_country || 'Kenya',
                mode_of_transport: form.mode_of_transport || 'Sea',
                currency: form.currency || 'KES',
                cover_start_date: form.cover_start_date || '',
                cover_end_date: form.cover_end_date || '',
                consignee_email: form.consignee_email || '',
                consignee_phone: form.consignee_phone || '',
                consignee_country_of_origin: form.consignee_country_of_origin || '',
                consignee_county: form.consignee_county || '',
                consignee_city: form.consignee_city || '',
                consignee_postal_code: form.consignee_postal_code || '',
                consignee_address: form.consignee_address || '',
                nature_of_goods: form.nature_of_goods || '',
                commercial_invoice_number: form.commercial_invoice_number || '',
                ucr_number: form.ucr_number || '',
                clearing_agent_pin: form.clearing_agent_pin || '',
                cargo_category: form.cargo_category || '',
                cargo_type: form.cargo_type || '',
                trade_type: form.trade_type || 'Import',
                transshipment_at: form.transshipment_at || '',
            };

            let idfData = null;
            try {
                const { data: idfRes } = await api.post('/cargo/idf-lookup', {
                    idf_number: form.idf_number.trim(),
                    agents_company_pin: selectedAgentPin.trim(),
                });
                idfData = idfRes;
            } catch (err) {
                console.warn('[CreateCover] IDF re-lookup after cover creation failed');
            }

            onComplete({ cover: data, idfData, coverFormData });
        } catch (err) {
            setError(err.response?.data?.error || 'Submission failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const today = todayLocalISO();
    const maxEndDate = getMaxEndDate();
    const modeLabel = MODE_LABELS[form.mode_of_transport] || MODE_LABELS.Sea;

    const completedSections = useMemo(() => {
        const done = {};
        SECTIONS.forEach((s) => {
            const errs = validateSection(s.id);
            done[s.id] = Object.keys(errs).length === 0;
        });
        return done;
    }, [form]);

    const progressPercent = useMemo(() => {
        const total = SECTIONS.length;
        const done = Object.values(completedSections).filter(Boolean).length;
        return Math.round((done / total) * 100);
    }, [completedSections]);

    const currentSectionIndex = SECTIONS.findIndex((s) => s.id === activeSection);
    const isLastSection = currentSectionIndex === SECTIONS.length - 1;

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn" ref={formTopRef}>
            {/* ============ HERO HEADER ============ */}
            <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-2xl shadow-[#3a663f]/25 mb-3 relative">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 to-transparent" />
                    <svg className="w-10 h-10 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                </div>
                <h1 className="text-3xl md:text-4xl font-extrabold text-gray-800 dark:text-white tracking-tight">
                    {prefillData ? 'Convert Quote to Cover' : 'New Marine Cover'}
                </h1>
                <p className="text-gray-500 dark:text-slate-400 max-w-xl mx-auto">
                    {prefillData
                        ? `Pre-filling from Quote #${prefillData.quote_number}`
                        : 'Complete the form below to create a marine cargo insurance cover with '}
                    <strong className="text-[#3a663f] dark:text-green-400">Mayfair Insurance</strong>
                </p>

                <div className="flex flex-wrap items-center justify-center gap-2">
                    {prefillData && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Quote #{prefillData.quote_number} • {new Date(prefillData.approved_at).toLocaleDateString()}
                        </span>
                    )}
                    {initialFormData && Object.keys(initialFormData).length > 0 && (
                        <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium rounded-full">
                            💾 Draft Restored
                        </span>
                    )}
                </div>
            </div>

            {/* ============ PROGRESS BAR ============ */}
            <div className="max-w-md mx-auto">
                <div className="flex items-center justify-between text-xs text-gray-400 dark:text-slate-500 mb-1.5">
                    <span>Form Completion</span>
                    <span className="font-medium text-[#3a663f] dark:text-green-400">{progressPercent}%</span>
                </div>
                <div className="w-full h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>

            {/* ============ FORM CARD ============ */}
            <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                {/* Stepper */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-gray-50 to-white dark:from-slate-700/30 dark:to-slate-800 overflow-x-auto">
                    <div className="flex items-center gap-1 min-w-max">
                        {SECTIONS.map((step, index) => {
                            const isActive = activeSection === step.id;
                            const isDone = completedSections[step.id];
                            return (
                                <React.Fragment key={step.id}>
                                    <button
                                        type="button"
                                        onClick={() => goToSection(step.id)}
                                        className={`flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg transition-all whitespace-nowrap ${isActive
                                            ? 'bg-[#3a663f] text-white shadow-md shadow-[#3a663f]/20'
                                            : isDone
                                                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                                                : 'text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-600'
                                            }`}
                                    >
                                        <span className="text-base">{isDone && !isActive ? '✓' : step.icon}</span>
                                        <span>{step.label}</span>
                                        {isActive && <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" />}
                                    </button>
                                    {index < SECTIONS.length - 1 && (
                                        <span className="text-gray-300 dark:text-slate-600 text-xs">▸</span>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Form Body */}
                <div className="p-6 md:p-8 space-y-8">
                    {error && (
                        <div
                            className={`flex items-start gap-3 px-4 py-3 rounded-xl animate-fadeIn ${error.startsWith('✅')
                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                                }`}
                        >
                            <span className="text-lg flex-shrink-0">{error.startsWith('✅') ? '✅' : '⚠️'}</span>
                            <span className="text-sm flex-1">{error.replace('✅ ', '')}</span>
                            <button type="button" onClick={() => setError('')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">✕</button>
                        </div>
                    )}

                    {activeSection === 'policy' && (
                        <SectionPolicy
                            form={form}
                            handleChange={handleChange}
                            handleModeChange={handleModeChange}
                            autoFilled={autoFilled}
                            formErrors={formErrors}
                            touched={touched}
                        />
                    )}

                    {activeSection === 'idf' && (
                        <SectionIDF
                            form={form}
                            handleChange={handleChange}
                            handleIDFLookup={handleIDFLookup}
                            idfLoading={idfLoading}
                            handleFileChange={handleFileChange}
                            clearingAgents={clearingAgents}
                            loadingAgents={loadingAgents}
                            selectedAgentPin={selectedAgentPin}
                            handleAgentSelect={handleAgentSelect}
                            onAgentPinInput={handleAgentPinInput}
                            showAddAgent={showAddAgent}
                            setShowAddAgent={setShowAddAgent}
                            newAgent={newAgent}
                            setNewAgent={setNewAgent}
                            handleSaveAgent={handleSaveAgent}
                            savingAgent={savingAgent}
                            agentError={agentError}
                            agentSuccess={agentSuccess}
                            formErrors={formErrors}
                            touched={touched}
                            autoFilled={autoFilled}
                        />
                    )}

                    {activeSection === 'vessel' && (
                        <SectionVessel form={form} handleChange={handleChange} formErrors={formErrors} touched={touched} autoFilled={autoFilled} />
                    )}

                    {activeSection === 'voyage' && (
                        <SectionVoyage
                            form={form}
                            handleChange={handleChange}
                            formErrors={formErrors}
                            touched={touched}
                            autoFilled={autoFilled}
                            modeLabel={modeLabel}
                        />
                    )}

                    {activeSection === 'cover' && (
                        <SectionCover
                            form={form}
                            handleChange={handleChange}
                            handleStartDateChange={handleStartDateChange}
                            today={today}
                            maxEndDate={maxEndDate}
                            isEndDateValid={isEndDateValid}
                            formErrors={formErrors}
                            touched={touched}
                            autoFilled={autoFilled}
                        />
                    )}

                    {activeSection === 'consignee' && (
                        <SectionConsignee form={form} handleChange={handleChange} formErrors={formErrors} touched={touched} autoFilled={autoFilled} />
                    )}

                    {isLastSection && (
                        <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    name="acknowledgment"
                                    checked={form.acknowledgment}
                                    onChange={handleChange}
                                    className="mt-1 w-4 h-4 text-[#3a663f] rounded border-gray-300 dark:border-slate-600 focus:ring-[#3a663f] focus:ring-2 transition-all"
                                />
                                <span className="text-sm text-gray-600 dark:text-slate-400 leading-relaxed group-hover:text-gray-800 dark:group-hover:text-slate-300 transition-colors">
                                    I acknowledge that any Personal Information supplied to{' '}
                                    <strong className="text-[#3a663f] dark:text-green-400">Mayfair Insurance</strong> is provided voluntarily and that Mayfair Insurance may not be able to comply with its obligations if the correct Personal Information is not supplied.
                                    <br />
                                    I warrant that all information, including Personal Information, supplied to Mayfair Insurance is accurate and current and agree to correct and update such information when necessary.
                                    <br />
                                    By submitting any Personal Information to Mayfair Insurance in any form, I acknowledge that such conduct constitutes a reasonable unconditional, specific and voluntary consent to the processing of such Personal Information.
                                </span>
                            </label>
                        </div>
                    )}
                </div>

                {/* Footer Nav */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        {currentSectionIndex > 0 && (
                            <button
                                type="button"
                                onClick={handleBack}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Back
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        {!isLastSection ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all transform active:scale-[0.98]"
                            >
                                Next: {SECTIONS[currentSectionIndex + 1]?.label}
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed group"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Creating Cover...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        {prefillData ? 'Convert to Cover' : 'Create Cover'}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
}

// ========== SECTION COMPONENTS ==========

function SectionHeader({ icon, title, subtitle }) {
    return (
        <div className="flex items-center gap-3 pb-2 border-b border-gray-100 dark:border-slate-700">
            <span className="text-2xl">{icon}</span>
            <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">{title}</h3>
                {subtitle && <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{subtitle}</p>}
            </div>
        </div>
    );
}

function AutoBadge() {
    return (
        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400">
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
            Auto
        </span>
    );
}

function SectionPolicy({ form, handleChange, handleModeChange, formErrors, touched, autoFilled }) {
    return (
        <div className="space-y-5 animate-fadeIn">
            <SectionHeader icon="📋" title="Policy Information" subtitle="Trade type, mode of transport, and currency" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <SelectField label="Trade Type" name="trade_type" value={form.trade_type} onChange={handleChange} options={['Import', 'Export']} required />
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Mode of Transport <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="mode_of_transport"
                        value={form.mode_of_transport}
                        onChange={handleModeChange}
                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                        required
                    >
                        <option value="Sea">Sea</option>
                        <option value="Air">Air</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Currency <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="currency"
                        value={form.currency}
                        onChange={handleChange}
                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                        required
                    >
                        <option value="KES">KES (KSh)</option>
                        <option value="USD">USD</option>
                    </select>
                    {formErrors.currency && touched.currency && (
                        <p className="text-xs text-red-500 mt-1.5">{formErrors.currency}</p>
                    )}
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Open Cover Number <span className="text-gray-400 text-xs">(optional)</span>
                    {autoFilled.open_cover_number && <AutoBadge />}
                </label>
                <input
                    type="text"
                    name="open_cover_number"
                    value={form.open_cover_number}
                    onChange={handleChange}
                    placeholder="e.g., OC-MS78G4PZ"
                    className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm"
                />
                <p className="text-xs text-gray-400 mt-1">Enter the open cover number if linked to an existing open cover</p>
            </div>
        </div>
    );
}

function SectionIDF({
    form, handleChange, handleIDFLookup, idfLoading, handleFileChange,
    clearingAgents, loadingAgents, selectedAgentPin, handleAgentSelect,
    onAgentPinInput, showAddAgent, setShowAddAgent, newAgent, setNewAgent,
    handleSaveAgent, savingAgent, agentError, agentSuccess, formErrors, touched, autoFilled,
}) {
    return (
        <div className="space-y-5 animate-fadeIn">
            <SectionHeader icon="🔍" title="IDF & Agent Details" subtitle="Lookup IDF to auto-fill voyage details" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        IDF Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            name="idf_number"
                            value={form.idf_number}
                            onChange={handleChange}
                            placeholder="e.g. 26MBAIM000010821"
                            className="flex-1 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all font-mono"
                            required
                        />
                        <button
                            type="button"
                            onClick={handleIDFLookup}
                            disabled={idfLoading}
                            className="bg-[#3a663f] hover:bg-[#2d5232] text-white font-medium px-6 rounded-xl transition-all disabled:opacity-60 text-sm flex items-center gap-2 shadow-md hover:shadow-lg whitespace-nowrap"
                        >
                            {idfLoading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Loading
                                </>
                            ) : '🔍 Query KRA ICMS'}
                        </button>
                    </div>
                    <AttachFileInput
                        label="IDF / UCR document"
                        name="idf_doc"
                        file={form.files.idf_doc}
                        onChange={handleFileChange}
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                    />
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                            Clearing Agent PIN <span className="text-red-500">*</span>
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowAddAgent(!showAddAgent)}
                            className="text-xs text-[#3a663f] dark:text-green-400 hover:text-[#2d5232] font-medium flex items-center gap-1 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            {showAddAgent ? 'Cancel' : 'Add New Agent'}
                        </button>
                    </div>

                    {loadingAgents ? (
                        <div className="h-12 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                    ) : clearingAgents.length > 0 ? (
                        <select
                            value={selectedAgentPin}
                            onChange={handleAgentSelect}
                            className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                            required
                        >
                            <option value="">— Select a clearing agent —</option>
                            {clearingAgents.map((agent) => (
                                <option key={agent.id} value={agent.kra_pin}>
                                    {agent.agent_name} — {agent.kra_pin}{agent.company_name ? ` (${agent.company_name})` : ''}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            value={selectedAgentPin}
                            onChange={onAgentPinInput}
                            placeholder="e.g., P000600883U"
                            className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none font-mono transition-all"
                            required
                        />
                    )}

                    {showAddAgent && (
                        <div className="mt-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-gray-200 dark:border-slate-600 animate-fadeIn">
                            <h4 className="font-semibold text-gray-800 dark:text-white mb-3 text-sm">➕ Add New Clearing Agent</h4>
                            {agentError && <p className="text-red-500 text-xs mb-2">{agentError}</p>}
                            {agentSuccess && <p className="text-green-600 text-xs mb-2">{agentSuccess}</p>}
                            <div className="grid grid-cols-2 gap-3">
                                <input type="text" placeholder="Agent Name *" value={newAgent.agent_name}
                                    onChange={(e) => setNewAgent({ ...newAgent, agent_name: e.target.value })}
                                    className="border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none" />
                                <input type="text" placeholder="KRA PIN *" value={newAgent.kra_pin}
                                    onChange={(e) => setNewAgent({ ...newAgent, kra_pin: e.target.value })}
                                    className="border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none" />
                                <input type="text" placeholder="Company Name" value={newAgent.company_name}
                                    onChange={(e) => setNewAgent({ ...newAgent, company_name: e.target.value })}
                                    className="border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none" />
                                <input type="text" placeholder="Phone" value={newAgent.phone}
                                    onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })}
                                    className="border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none" />
                                <button type="button" onClick={handleSaveAgent} disabled={savingAgent}
                                    className="col-span-2 bg-[#3a663f] hover:bg-[#2d5232] text-white rounded-lg px-4 py-2 text-sm font-medium transition-all disabled:opacity-60 shadow-sm hover:shadow-md">
                                    {savingAgent ? 'Saving...' : '💾 Save Agent'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        UCR Number
                        {autoFilled.ucr_number && <AutoBadge />}
                    </label>
                    <input type="text" name="ucr_number" value={form.ucr_number} onChange={handleChange}
                        placeholder="Auto-filled from IDF"
                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all font-mono" />
                    <AttachFileInput
                        label="Waybill document"
                        name="ucr_doc"
                        file={form.files.ucr_doc}
                        onChange={handleFileChange}
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Commercial Invoice Number <span className="text-red-500">*</span>
                        {autoFilled.commercial_invoice_number && <AutoBadge />}
                    </label>
                    <input type="text" name="commercial_invoice_number" value={form.commercial_invoice_number}
                        onChange={handleChange} placeholder="Auto-filled from IDF"
                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                        required />
                    <AttachFileInput
                        label="Commercial Invoice"
                        name="invoice_doc"
                        file={form.files.invoice_doc}
                        onChange={handleFileChange}
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                    />
                </div>
            </div>
        </div>
    );
}

function SectionVessel({ form, handleChange, formErrors, touched, autoFilled }) {
    return (
        <div className="space-y-5 animate-fadeIn">
            <SectionHeader icon="🚢" title="Vessel Information" subtitle="Details about the vessel carrying your cargo" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Vessel Name <span className="text-red-500">*</span>
                        {autoFilled.vessel_name && <AutoBadge />}
                    </label>
                    <input type="text" name="vessel_name" value={form.vessel_name} onChange={handleChange}
                        placeholder="Enter vessel name"
                        className={`w-full border ${formErrors.vessel_name && touched.vessel_name ? 'border-red-400' : 'border-gray-200 dark:border-slate-600'} dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all`} />
                    {formErrors.vessel_name && touched.vessel_name && <p className="text-xs text-red-500 mt-1.5">{formErrors.vessel_name}</p>}
                </div>
                <SelectField label="Cargo Category" name="cargo_category" value={form.cargo_category} onChange={handleChange}
                    options={['', 'General Cargo', 'Bulk Cargo', 'Containerized']} />
                <SelectField label="Cargo Type" name="cargo_type" value={form.cargo_type} onChange={handleChange}
                    options={['', 'Fragile', 'Perishable', 'Hazardous', 'Other']} />
            </div>
        </div>
    );
}

function SectionVoyage({ form, handleChange, formErrors, touched, autoFilled, modeLabel }) {
    return (
        <div className="space-y-5 animate-fadeIn">
            <SectionHeader icon="🌍" title="Voyage Details" subtitle="Route, ports, transshipment, and final destination" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Voyage From <span className="text-red-500">*</span>
                        {autoFilled.voyage_from_country && <AutoBadge />}
                    </label>
                    <input type="text" name="voyage_from_country" value={form.voyage_from_country} onChange={handleChange}
                        placeholder="Country of origin"
                        className={`w-full border ${formErrors.voyage_from_country && touched.voyage_from_country ? 'border-red-400' : 'border-gray-200 dark:border-slate-600'} dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all`} />
                    {formErrors.voyage_from_country && touched.voyage_from_country && <p className="text-xs text-red-500 mt-1.5">{formErrors.voyage_from_country}</p>}
                </div>
                <div>
                    <ModeAwarePortSelect
                        name="loading_at"
                        label={modeLabel.loading}
                        value={form.loading_at}
                        onChange={handleChange}
                        placeholder={`Search for a ${modeLabel.loading.toLowerCase()}...`}
                        isRequired
                        error={formErrors.loading_at}
                        touched={touched.loading_at}
                        mode={form.mode_of_transport}
                    />
                    {autoFilled.loading_at && <AutoBadge />}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Voyage To <span className="text-red-500">*</span>
                    </label>
                    <input type="text" name="voyage_to_country" value={form.voyage_to_country} onChange={handleChange}
                        readOnly
                        className="w-full border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 outline-none" />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <ModeAwarePortSelect
                        name="port_of_discharge"
                        label={modeLabel.discharge}
                        value={form.port_of_discharge}
                        onChange={handleChange}
                        placeholder={`Search for a ${modeLabel.discharge.toLowerCase()}...`}
                        isRequired
                        error={formErrors.port_of_discharge}
                        touched={touched.port_of_discharge}
                        mode={form.mode_of_transport}
                    />
                    {autoFilled.port_of_discharge && <AutoBadge />}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Final Destination <span className="text-red-500">*</span>
                        {autoFilled.final_destination && <AutoBadge />}
                    </label>
                    <input
                        type="text"
                        name="final_destination"
                        value={form.final_destination}
                        onChange={handleChange}
                        placeholder="e.g. Nairobi ICD, Kampala, Kigali"
                        className={`w-full border ${formErrors.final_destination && touched.final_destination ? 'border-red-400' : 'border-gray-200 dark:border-slate-600'} dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all`}
                        required
                    />
                    {formErrors.final_destination && touched.final_destination && (
                        <p className="text-xs text-red-500 mt-1.5">{formErrors.final_destination}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">City / ICD / warehouse where the goods finally end up</p>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Transshipment At
                    </label>
                    <input type="text" name="transshipment_at" value={form.transshipment_at} onChange={handleChange}
                        placeholder="If any"
                        className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all" />
                </div>
            </div>
        </div>
    );
}

function SectionCover({ form, handleChange, handleStartDateChange, today, maxEndDate, isEndDateValid, formErrors, touched, autoFilled }) {
    return (
        <div className="space-y-5 animate-fadeIn">
            <SectionHeader icon="📅" title="Cover Period" subtitle="Fixed 90-day coverage" />
            <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    End date is automatically set to <strong>90 calendar days</strong> after the start date
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Cover Start Date <span className="text-red-500">*</span>
                    </label>
                    <input type="date" name="cover_start_date" value={form.cover_start_date} onChange={handleStartDateChange}
                        min={today}
                        className={`w-full border ${formErrors.cover_start_date && touched.cover_start_date ? 'border-red-400' : 'border-gray-200 dark:border-slate-600'} dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all`} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Cover End Date <span className="text-red-500">*</span>
                    </label>
                    <input type="date" name="cover_end_date" value={form.cover_end_date} onChange={handleChange}
                        min={form.cover_start_date || today}
                        max={maxEndDate}
                        readOnly
                        className="w-full border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 outline-none" />
                </div>
            </div>
            {form.cover_start_date && form.cover_end_date && (
                <div className={`text-xs ${isEndDateValid() ? 'text-green-500' : 'text-red-500'}`}>
                    {isEndDateValid() ? '✅ Cover period is exactly 90 days' : '⚠️ Cover period must be exactly 90 days'}
                </div>
            )}
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                    Nature of Goods <span className="text-red-500">*</span>
                    {autoFilled.nature_of_goods && <AutoBadge />}
                </label>
                <textarea name="nature_of_goods" value={form.nature_of_goods} onChange={handleChange} rows={4}
                    placeholder="Describe the nature of goods being transported..."
                    className={`w-full border ${formErrors.nature_of_goods && touched.nature_of_goods ? 'border-red-400' : 'border-gray-200 dark:border-slate-600'} dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none resize-none transition-all`} />
                {formErrors.nature_of_goods && touched.nature_of_goods && <p className="text-xs text-red-500 mt-1.5">{formErrors.nature_of_goods}</p>}
            </div>
        </div>
    );
}

function SectionConsignee({ form, handleChange, formErrors, touched, autoFilled }) {
    const fieldProps = (name, label, extra = {}) => ({
        label,
        name,
        value: form[name],
        onChange: handleChange,
        error: formErrors[name],
        touched: touched[name],
        autoFilled: autoFilled[name],
        ...extra,
    });

    return (
        <div className="space-y-5 animate-fadeIn">
            <SectionHeader icon="👤" title="Consignee Contact Information" subtitle="All fields required for certificate issuance" />
            <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    These details appear on your certificate and are shared with DMVIC
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <InputField {...fieldProps('consignee_email', 'Email', { type: 'email', placeholder: 'consignee@example.com', required: true })} />
                <InputField {...fieldProps('consignee_phone', 'Phone Number', { placeholder: '+254 700 111 222', required: true })} />
                <InputField {...fieldProps('consignee_country_of_origin', 'Country of Origin', { placeholder: 'e.g., Kenya', required: true })} />
                <InputField {...fieldProps('consignee_county', 'County', { placeholder: 'e.g., Nairobi', required: true })} />
                <InputField {...fieldProps('consignee_city', 'City/Town', { placeholder: 'e.g., Nairobi', required: true })} />
                <InputField {...fieldProps('consignee_postal_code', 'Postal Code', { placeholder: 'e.g., 00100', required: true })} />
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                        Address <span className="text-red-500">*</span>
                        {autoFilled.consignee_address && <AutoBadge />}
                    </label>
                    <textarea
                        name="consignee_address"
                        value={form.consignee_address}
                        onChange={handleChange}
                        rows={2}
                        placeholder="Moi Avenue, Bima Towers 4th Floor"
                        className={`w-full border ${formErrors.consignee_address && touched.consignee_address ? 'border-red-400' : 'border-gray-200 dark:border-slate-600'} dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm resize-none`}
                        required
                    />
                    {formErrors.consignee_address && touched.consignee_address && (
                        <p className="text-xs text-red-500 mt-1.5">{formErrors.consignee_address}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

// ========== REUSABLE FIELDS ==========

function InputField({ label, name, value, onChange, type = 'text', placeholder = '', required = false, readOnly = false, bg, min, max, error, touched, autoFilled }) {
    const showError = error && touched;
    const bgClass = bg || 'bg-white dark:bg-slate-700';

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                {label} {required && <span className="text-red-500">*</span>}
                {autoFilled && <AutoBadge />}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                readOnly={readOnly}
                required={required}
                min={min}
                max={max}
                className={`w-full border ${showError ? 'border-red-400' : 'border-gray-200 dark:border-slate-600'} ${bgClass} rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm dark:text-white`}
                aria-invalid={showError ? 'true' : 'false'}
            />
            {showError && <p className="text-xs text-red-500 mt-1.5">{error}</p>}
        </div>
    );
}

function SelectField({ label, name, value, onChange, options, required = false }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <select
                name={name}
                value={value}
                onChange={onChange}
                className="w-full border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm"
                required={required}
            >
                {options.map((opt, i) => (
                    <option key={i} value={opt}>{opt || '— Select —'}</option>
                ))}
            </select>
        </div>
    );
}